import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

interface Location {
  id: string;
  name: string;
}

interface EventLocation {
  location_id: string;
  location_type: 'meeting_point' | 'other' | 'lunch' | 'drink/dinner';
  display_order: number;
}

interface EventLocationsProps {
  eventId?: string;
  onChange: (locations: EventLocation[]) => void;
}

const EventLocations = ({ eventId, onChange }: EventLocationsProps) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<EventLocation[]>([]);
  const [newLocationName, setNewLocationName] = useState("");
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchLocations = async () => {
      const { data } = await supabase.from('locations').select('*').order('name', { ascending: true });
      if (data) setLocations(data);
    };

    const fetchEventLocations = async () => {
      if (!eventId) return;
      const { data } = await supabase
        .from('event_locations')
        .select('*')
        .eq('event_id', eventId);
      if (data) {
        setSelectedLocations(data.map(loc => ({
          location_id: loc.location_id,
          location_type: loc.location_type,
          display_order: loc.display_order
        })));
      }
    };

    fetchLocations();
    fetchEventLocations();
  }, [eventId]);

  const addLocation = () => {
    const newLocation: EventLocation = {
      location_id: '',
      location_type: 'other',
      display_order: selectedLocations.length
    };
    setSelectedLocations([...selectedLocations, newLocation]);
    onChange([...selectedLocations, newLocation]);
  };

  const removeLocation = (index: number) => {
    const updated = selectedLocations.filter((_, i) => i !== index);
    setSelectedLocations(updated);
    onChange(updated);
  };

  const updateLocation = (index: number, field: keyof EventLocation, value: string | number) => {
    const updated = selectedLocations.map((loc, i) => {
      if (i === index) {
        return { ...loc, [field]: value };
      }
      return loc;
    });
    setSelectedLocations(updated);
    onChange(updated);
  };

  const handleAddNewLocation = async () => {
    if (!newLocationName.trim()) {
      toast({
        title: "Error",
        description: "Location name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .insert([{ name: newLocationName.trim() }])
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setLocations([...locations, data]);
        setNewLocationName("");
        setIsAddingLocation(false);
        toast({
          title: "Success",
          description: "New location added successfully",
        });
      }
    } catch (error) {
      console.error('Error adding new location:', error);
      toast({
        title: "Error",
        description: "Failed to add new location",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <Label>Event Locations</Label>

      <div className="space-y-4">
        {selectedLocations.map((loc, index) => (
          <div key={index} className="flex gap-4 items-start">
            <div className="flex-1">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={loc.location_id}
                onChange={(e) => updateLocation(index, 'location_id', e.target.value)}
              >
                <option value="">Select a location</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            <select
              className="flex h-10 w-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              value={loc.location_type}
              onChange={(e) => updateLocation(index, 'location_type', e.target.value as any)}
            >
              <option value="meeting_point">Meeting Point</option>
              <option value="other">Other</option>
              <option value="lunch">Lunch</option>
              <option value="drink/dinner">Drink/Dinner</option>
            </select>

            <Button 
              type="button" 
              variant="ghost" 
              size="icon"
              onClick={() => removeLocation(index)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={addLocation}
        >
          Pick Existing Location
        </Button>

        {isAddingLocation ? (
          <div className="flex gap-2 flex-1">
            <Input
              placeholder="Enter new location name"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
            />
            <Button 
              type="button"
              onClick={handleAddNewLocation}
            >
              Add
            </Button>
            <Button 
              type="button"
              variant="ghost"
              onClick={() => {
                setIsAddingLocation(false);
                setNewLocationName("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsAddingLocation(true)}
          >
            Add New Location
          </Button>
        )}
      </div>
    </div>
  );
};

export default EventLocations;